import type { Event } from 'models';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from 'permissions';
import { Button, Form, UserAddLineIcon, message } from 'ui';
import { EventInviteesFooter } from '../../../components';
import { INDIVIDUAL_INVITATION_LIMIT } from '../../../invitations-form-content';
import { useEventInviteesCount } from '../../hooks';
import { EventInviteesFilters, type EventInviteesFiltersFormValues } from '../event-invitees-filters';
import { EventInviteesList } from '../event-invitees-list';

type FormValues = EventInviteesFiltersFormValues;

const initialValues: Partial<FormValues> = {
  role: 'all',
  status: 'all',
  type: 'individual',
};

type P = {
  event: Event;
  onOpenInviteModal?: () => void;
};

const EventInviteesForm = ({ event, onOpenInviteModal }: P) => {
  const { distributionList, goingCount, notGoingCount, pendingCount } = event;
  const { canInviteUserToEvent, isEventsAdmin } = usePermissions();
  const hasFilter = event.type !== 'telehealth_visit';
  const hasDistributionList = distributionList ? distributionList.length > 0 : false;
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const eventInviteesCount = useEventInviteesCount(event);

  const role = Form.useWatch('role', form);
  const search = Form.useWatch('search', form);
  const status = Form.useWatch('status', form);
  const type = Form.useWatch('type', form);

  const handleInviteMorePeople = useCallback(() => {
    if (!isEventsAdmin({}) && eventInviteesCount >= INDIVIDUAL_INVITATION_LIMIT) {
      message.error(
        t("You've reached the {{count}}-invitation limit for your role. Remove someone to add another.", {
          count: INDIVIDUAL_INVITATION_LIMIT,
        }),
      );
      return;
    }
    onOpenInviteModal?.();
  }, [eventInviteesCount, isEventsAdmin, onOpenInviteModal, t]);

  return (
    <Form<FormValues> form={form} initialValues={initialValues} layout="vertical">
      {hasFilter ? <EventInviteesFilters hasDistributionList={hasDistributionList} type={type} /> : null}

      <EventInviteesList
        event={event}
        filter={{
          role: role === 'all' ? undefined : role,
          search,
          status: status === 'all' ? undefined : status,
          type,
        }}
      />

      <EventInviteesFooter
        extra={
          onOpenInviteModal && canInviteUserToEvent({ event }) ? (
            <Button icon={<UserAddLineIcon />} onClick={handleInviteMorePeople} size="medium" type="primary">
              Invite more
            </Button>
          ) : null
        }
        goingInviteesCount={goingCount}
        notGoingInviteesCount={notGoingCount}
        pendingInviteesCount={pendingCount}
      />
    </Form>
  );
};

export default EventInviteesForm;
